// frontend/src/alicuotas/AdminAlicuotas.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Badge, Spinner, Dropdown } from 'react-bootstrap';
import {
  FaFileInvoiceDollar, FaCheck, FaTimes, FaEdit, FaTrash, FaPlus,
  FaFilter, FaChartLine, FaExclamationTriangle, FaClock, FaCheckCircle,
  FaFileExport, FaPrint, FaFilePdf, FaFileExcel, FaFileCsv,
  FaChevronLeft, FaChevronRight, FaDollarSign, FaCalendarAlt,
  FaUser, FaBuilding, FaQrcode, FaMoneyBillWave, FaListAlt, FaHourglassHalf
} from 'react-icons/fa';
import './AdminAlicuotas.css';
import '../styles/AdminTabs.css';
import { API_URL, API_ENDPOINTS } from '../config/api';
import PaymentConfigModal from '../admin/PaymentConfigModal';

// --- Funciones Auxiliares ---
const formatCurrency = (amount) => {
  if (!amount) return '$0.00';
  return `$${Number(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    const parts = dateString.split('-');
    let adjustedDate = date;
    if (parts.length === 3 && dateString.length === 10) {
      adjustedDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    } else if (isNaN(date.getTime()) || date.getTime() === 0) {
      return '-';
    }
    return adjustedDate.toLocaleDateString('es-EC', { timeZone: 'America/Guayaquil' });
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return 'Fecha inválida';
  }
};

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const parts = dateString.split('-');
    let adjustedDate = date;
    if (parts.length === 3 && dateString.length === 10) {
      adjustedDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    } else if (isNaN(date.getTime())) {
      return '';
    }
    const year = adjustedDate.getFullYear();
    const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
    const day = String(adjustedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    console.error("Error formatting date for input:", dateString, e);
    return '';
  }
};

const getToken = () => localStorage.getItem('token') || '';

// --- Función para exportar a CSV ---
const exportToCSV = (data, filename = 'alicuotas.csv') => {
  const headers = ['ID', 'Concepto', 'Monto', 'Fecha Vencimiento', 'Apartamento', 'Usuario', 'Estado'];
  const csvContent = [
    headers.join(','),
    ...data.map(row => [
      row.id,
      `"${row.concepto}"`,
      row.monto,
      row.fechaVencimiento,
      row.apartamento,
      `"${row.usuario}"`,
      row.estado
    ].join(','))
  ].join('\n');

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

// --- Función para imprimir ---
const printTable = (data) => {
  const printWindow = window.open('', '', 'height=600,width=800');
  const tableHTML = `
    <html>
    <head>
      <title>Alícuotas - Reporte</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { text-align: center; color: #2D2770; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
        th { background-color: #2D2770; color: white; }
        tr:nth-child(even) { background-color: #f2f2f2; }
        .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
      </style>
    </head>
    <body>
      <h1>Reporte de Alícuotas</h1>
      <p><strong>Fecha:</strong> ${new Date().toLocaleDateString('es-EC')}</p>
      <p><strong>Total registros:</strong> ${data.length}</p>
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Concepto</th>
            <th>Monto</th>
            <th>Fecha Venc.</th>
            <th>Apto</th>
            <th>Usuario</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          ${data.map(row => `
            <tr>
              <td>${row.id}</td>
              <td>${row.concepto}</td>
              <td>${formatCurrency(row.monto)}</td>
              <td>${row.fechaVencimiento}</td>
              <td>${row.apartamento}</td>
              <td>${row.usuario}</td>
              <td>${row.estado}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      <div class="footer">
        <p>Generado por UrbanQ - Sistema de Gestión de Condominios</p>
      </div>
    </body>
    </html>
  `;
  printWindow.document.write(tableHTML);
  printWindow.document.close();
  printWindow.print();
};

// --- Componente AdminAlicuotas ---
const AdminAlicuotas = () => {
  // Estados
  const [alicuotas, setAlicuotas] = useState([]);
  const [filteredAlicuotas, setFilteredAlicuotas] = useState([]);
  const [displayedAlicuotas, setDisplayedAlicuotas] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState(null);
  const [errorUsers, setErrorUsers] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAlicuota, setSelectedAlicuota] = useState(null);
  const [newAlicuotaData, setNewAlicuotaData] = useState({
    concepto: '',
    descripcion: '',
    monto: '',
    fechaVencimiento: '',
    usuarioId: ''
  });

  const [showApproveModal, setShowApproveModal] = useState(false);

  // NUEVOS ESTADOS PARA CONFIGURACIÓN DE PAGOS
  const [showPaymentConfigModal, setShowPaymentConfigModal] = useState(false);
  const [paymentConfigExists, setPaymentConfigExists] = useState(null);

  const [stats, setStats] = useState({
    pagosRealizados: 0,
    pagosPendientes: 0,
    pendientesAprobacion: 0,
    total: 0,
    progreso: 0,
    montoTotal: 0,
    montoPendiente: 0,
    montoPagado: 0
  });
  
  const [activeFilter, setActiveFilter] = useState('todos');
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // NUEVA FUNCIÓN: Verificar configuración de pago
  const checkPaymentConfig = async () => {
    try {
      const token = getToken();
      if (!token) return;
      
      const response = await fetch(API_ENDPOINTS.paymentConfigCheck, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPaymentConfigExists(data.exists);
        
        // Si no existe configuración, mostrar modal
        if (!data.exists) {
          setShowPaymentConfigModal(true);
        }
      }
    } catch (err) {
      console.error('Error al verificar configuración de pago:', err);
    }
  };

  // --- Cargar datos ---
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setLoadingUsers(true);

    const fetchData = async () => {
      if (!isMounted) return;
      setError(null);
      const token = getToken();
      if (!token) {
        if (isMounted) setError('No autorizado. Token no encontrado.');
        return;
      }
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

      try {
        const response = await fetch(API_ENDPOINTS.payments, { headers });
        if (!isMounted) return;
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error ${response.status} (Pagos): ${errorText}`);
        }
        const data = await response.json();
        const formattedAlicuotas = data.payments.map(payment => ({
          id: payment.id,
          concepto: payment.concept,
          monto: payment.amount,
          fechaOriginal: payment.due_date || payment.created_at,
          fechaVencimiento: formatDate(payment.due_date || payment.created_at),
          descripcion: payment.description || '',
          apartamento: payment.apartment || 'N/A',
          usuario: payment.user_name || payment.username || 'Usuario Desconocido',
          usuarioId: payment.user_id,
          estado: payment.status === 'Paid' ? 'Pagado' : payment.status === 'PendingApproval' ? 'Pendiente de validación' : 'Pendiente'
        }));

        if (isMounted) {
          setAlicuotas(formattedAlicuotas);
          
          if (activeFilter === 'todos') setFilteredAlicuotas(formattedAlicuotas);
          else setFilteredAlicuotas(formattedAlicuotas.filter(a => a.estado.toLowerCase().replace(/ /g,'') === activeFilter.toLowerCase().replace(/ /g,'')));
          
          const pagados = formattedAlicuotas.filter(a => a.estado === 'Pagado');
          const pendientes = formattedAlicuotas.filter(a => a.estado === 'Pendiente');
          const pendientesValidacion = formattedAlicuotas.filter(a => a.estado === 'Pendiente de validación');
          const total = formattedAlicuotas.length;
          
          const montoTotal = formattedAlicuotas.reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
          const montoPagado = pagados.reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
          const montoPendiente = pendientes.reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
          
          setStats({ 
            pagosRealizados: pagados.length,
            pagosPendientes: pendientes.length,
            pendientesAprobacion: pendientesValidacion.length,
            total,
            montoTotal,
            montoPagado,
            montoPendiente,
            progreso: total > 0 ? Math.round(((pagados.length + pendientesValidacion.length) / total) * 100) : 0
          });
        }
      } catch (err) {
        console.error('Error al cargar pagos:', err);
        if (isMounted) setError(err.message || 'Error al cargar pagos');
      } finally {
        if (isMounted && !loadingUsers) setLoading(false);
      }
    };

    const fetchUsers = async () => {
      if (!isMounted) return;
      setErrorUsers(null);
      const token = getToken();
      if (!token) {
        if (isMounted) setErrorUsers('No autorizado para cargar usuarios.');
        if (isMounted) setLoadingUsers(false);
        return;
      }
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      try {
        const response = await fetch(API_ENDPOINTS.users, { headers });
        if (!isMounted) return;
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error ${response.status} (Usuarios): ${errorText}`);
        }
        const userData = await response.json();
        let usersArray = [];
        if (userData && Array.isArray(userData.users)) usersArray = userData.users;
        else if (Array.isArray(userData)) usersArray = userData;
        if (isMounted) setUsers(usersArray);
      } catch (err) {
        console.error('Error al cargar usuarios:', err);
        if (isMounted) setErrorUsers(err.message || 'Error al cargar usuarios');
      } finally {
        if (isMounted) setLoadingUsers(false);
      }
    };

    fetchData();
    fetchUsers();
    
    // AGREGAR: Verificar configuración de pago
    checkPaymentConfig();
    
    const intervalId = setInterval(fetchData, 30000);
    
    return () => { 
      isMounted = false; 
      clearInterval(intervalId); 
    };
  }, [activeFilter]);

  useEffect(() => {
    if (!loadingUsers && loading) setLoading(false);
  }, [loadingUsers, loading]);

  useEffect(() => {
    const pages = Math.ceil(filteredAlicuotas.length / itemsPerPage);
    setTotalPages(pages || 1);
    
    if (currentPage > pages && pages > 0) setCurrentPage(pages);
    
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setDisplayedAlicuotas(filteredAlicuotas.slice(indexOfFirstItem, indexOfLastItem));
  }, [filteredAlicuotas, currentPage, itemsPerPage]);

  const updateStateAfterChange = (updatedAlicuotaList) => {
    setAlicuotas(updatedAlicuotaList);
    
    if (activeFilter === 'todos') setFilteredAlicuotas(updatedAlicuotaList);
    else setFilteredAlicuotas(updatedAlicuotaList.filter(a => a.estado.toLowerCase().replace(/ /g,'') === activeFilter.toLowerCase().replace(/ /g,'')));
    
    const pagados = updatedAlicuotaList.filter(a => a.estado === 'Pagado');
    const pendientes = updatedAlicuotaList.filter(a => a.estado === 'Pendiente');
    const pendientesValidacion = updatedAlicuotaList.filter(a => a.estado === 'Pendiente de validación');
    const total = updatedAlicuotaList.length;
    
    const montoTotal = updatedAlicuotaList.reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
    const montoPagado = pagados.reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
    const montoPendiente = pendientes.reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
    
    setStats({ 
      pagosRealizados: pagados.length,
      pagosPendientes: pendientes.length,
      pendientesAprobacion: pendientesValidacion.length,
      total,
      montoTotal,
      montoPagado,
      montoPendiente,
      progreso: total > 0 ? Math.round(((pagados.length + pendientesValidacion.length) / total) * 100) : 0
    });
  };

  // Handlers
  const handleOpenModal = (alicuota = null) => {
    // AGREGAR: Validar configuración antes de crear alícuotas
    if (paymentConfigExists === false) {
      setError('Debes configurar al menos un método de pago antes de crear alícuotas');
      setShowPaymentConfigModal(true);
      return;
    }
    
    if (alicuota) {
      setIsEditMode(true);
      setSelectedAlicuota(alicuota);
      setNewAlicuotaData({
        concepto: alicuota.concepto,
        descripcion: alicuota.descripcion || '',
        monto: alicuota.monto,
        fechaVencimiento: formatDateForInput(alicuota.fechaOriginal),
        usuarioId: alicuota.usuarioId
      });
    } else {
      setIsEditMode(false);
      setSelectedAlicuota(null);
      setNewAlicuotaData({
        concepto: '',
        descripcion: '',
        monto: '',
        fechaVencimiento: '',
        usuarioId: ''
      });
    }
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };
  
  const handleCloseModal = () => setShowModal(false);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAlicuotaData(prev => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  // NUEVA FUNCIÓN: Handler cuando se guarda configuración
  const handlePaymentConfigSaved = () => {
    setPaymentConfigExists(true);
    setShowPaymentConfigModal(false);
    setSuccess('Configuración de pago guardada exitosamente. Ahora puedes crear alícuotas.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    const token = getToken();
    if (!token) {
      setError('No autorizado.');
      setLoading(false);
      return;
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const bodyData = {
      user_id: newAlicuotaData.usuarioId,
      concept: newAlicuotaData.concepto,
      amount: newAlicuotaData.monto,
      description: newAlicuotaData.descripcion || null,
      due_date: newAlicuotaData.fechaVencimiento || null
    };

    const url = isEditMode ? API_ENDPOINTS.paymentsById(selectedAlicuota.id) : API_ENDPOINTS.payments;
    const method = isEditMode ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || `Error ${response.status} al ${isEditMode ? 'actualizar' : 'crear'} la alícuota`);
      }

      setSuccess(`Alícuota ${isEditMode ? 'actualizada' : 'creada'} exitosamente.`);

      if (isEditMode) {
        const updatedList = alicuotas.map(a =>
          a.id === selectedAlicuota.id
            ? {
                ...a,
                ...responseData.payment,
                concepto: bodyData.concept,
                monto: bodyData.amount,
                descripcion: bodyData.description,
                fechaOriginal: bodyData.due_date,
                fechaVencimiento: formatDate(bodyData.due_date),
                usuarioId: bodyData.user_id,
                usuario: users.find(u => u.id === parseInt(bodyData.user_id))?.name || selectedAlicuota.usuario,
                apartamento: users.find(u => u.id === parseInt(bodyData.user_id))?.apartment || selectedAlicuota.apartamento,
                estado: selectedAlicuota.estado
              }
            : a
        );
        updateStateAfterChange(updatedList);
      } else {
        const newItemFormatted = {
          id: responseData.payment.id,
          concepto: responseData.payment.concept,
          monto: responseData.payment.amount,
          fechaOriginal: responseData.payment.due_date || responseData.payment.created_at,
          fechaVencimiento: formatDate(responseData.payment.due_date || responseData.payment.created_at),
          descripcion: responseData.payment.description || '',
          usuarioId: responseData.payment.user_id,
          estado: responseData.payment.status === 'Paid' ? 'Pagado' : responseData.payment.status === 'PendingApproval' ? 'Pendiente de validación' : 'Pendiente',
          usuario: users.find(u => u.id === parseInt(responseData.payment.user_id))?.name || 'Usuario Desconocido',
          apartamento: users.find(u => u.id === parseInt(responseData.payment.user_id))?.apartment || 'N/A',
        };
        updateStateAfterChange([newItemFormatted, ...alicuotas]);
      }

      handleCloseModal();

    } catch (err) {
      console.error(`Error en handleSubmit (${method}):`, err);
      setError(err.message || `Ocurrió un error inesperado al ${isEditMode ? 'actualizar' : 'crear'}.`);
    } finally {
      setLoading(false);
      if (success) setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleDeleteAlicuota = async (id) => {
    if (!window.confirm(`¿Seguro que deseas eliminar la alícuota con ID ${id}?`)) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    const token = getToken();
    if (!token) {
      setError('No autorizado.');
      setLoading(false);
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };
    const url = API_ENDPOINTS.paymentsById(id);

    try {
      const response = await fetch(url, { method: 'DELETE', headers: headers });
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || `Error ${response.status} al eliminar`);
      }

      setSuccess(`Alícuota ${id} eliminada exitosamente.`);
      const updatedList = alicuotas.filter(a => a.id !== id);
      updateStateAfterChange(updatedList);

    } catch (err) {
      console.error('Error en handleDeleteAlicuota:', err);
      setError(err.message || 'Error al eliminar.');
    } finally {
      setLoading(false);
      if (success) setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleOpenApproveModal = (alicuota) => {
    setSelectedAlicuota(alicuota);
    setShowApproveModal(true);
    setError(null);
    setSuccess(null);
  };
  
  const handleCloseApproveModal = () => {
    setShowApproveModal(false);
    setSelectedAlicuota(null);
  };

  const handleApprovePayment = async () => {
    if (!selectedAlicuota) return;
    setError(null);
    setSuccess(null);
    setLoading(true);

    const token = getToken();
    if (!token) {
      setError('No autorizado.');
      setLoading(false);
      return;
    }
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const url = API_ENDPOINTS.paymentsStatus(selectedAlicuota.id);

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({ 
          status: 'Paid', 
          paymentDate: new Date().toISOString() 
        })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || `Error ${response.status} al aprobar pago`);
      }
      
      setSuccess(`Pago ${selectedAlicuota.id} aprobado exitosamente.`);
      
      const updatedList = alicuotas.map(a => 
        a.id === selectedAlicuota.id ? { ...a, estado: 'Pagado' } : a
      );
      
      updateStateAfterChange(updatedList);
      handleCloseApproveModal();
    } catch (err) {
      console.error('Error en handleApprovePayment:', err);
      setError(err.message || 'Error al aprobar el pago.');
    } finally {
      setLoading(false);
      if (success) setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleRejectPayment = async (alicuota) => {
    if (!alicuota || !window.confirm(`¿Rechazar pago ${alicuota.id}?`)) return;
    setError(null);
    setSuccess(null);
    setLoading(true);

    const token = getToken();
    if (!token) { 
      setError('No autorizado.'); 
      setLoading(false); 
      return; 
    }
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const url = API_ENDPOINTS.paymentsStatus(alicuota.id);

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({ 
          status: 'Pending', 
          paymentDate: null 
        })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || `Error ${response.status} al rechazar`);
      }
      
      setSuccess(`Pago ${alicuota.id} rechazado.`);
      
      const updatedList = alicuotas.map(a => 
        a.id === alicuota.id ? { ...a, estado: 'Pendiente' } : a
      );
      
      updateStateAfterChange(updatedList);
    } catch (err) {
      console.error('Error en handleRejectPayment:', err);
      setError(err.message || 'Error al rechazar.');
    } finally {
      setLoading(false);
      if (success) setTimeout(() => setSuccess(null), 3000);
    }
  };

  const filterAlicuotas = (estado) => {
    setActiveFilter(estado);
    setCurrentPage(1);
    
    if (estado === 'todos') {
      setFilteredAlicuotas(alicuotas);
    } else {
      setFilteredAlicuotas(alicuotas.filter(a => 
        a.estado.toLowerCase().replace(/ /g,'') === estado.toLowerCase().replace(/ /g,'')
      ));
    }
  };

  return (
    <div className="urbanq-alicuotas-container">
      {/* Tabs de navegación */}
      <div className="urbanq-admin-tabs">
        <button
          className={`urbanq-admin-tab ${activeFilter === 'todos' ? 'active' : ''}`}
          onClick={() => filterAlicuotas('todos')}
        >
          <FaListAlt /> Todas ({stats.total})
        </button>
        <button
          className={`urbanq-admin-tab ${activeFilter === 'Pagado' ? 'active' : ''}`}
          onClick={() => filterAlicuotas('Pagado')}
        >
          <FaCheckCircle /> Pagadas ({stats.pagosRealizados})
        </button>
        <button
          className={`urbanq-admin-tab ${activeFilter === 'Pendiente de validación' ? 'active' : ''}`}
          onClick={() => filterAlicuotas('Pendiente de validación')}
        >
          <FaHourglassHalf /> En Validación ({stats.pendientesAprobacion})
        </button>
        <button
          className={`urbanq-admin-tab ${activeFilter === 'Pendiente' ? 'active' : ''}`}
          onClick={() => filterAlicuotas('Pendiente')}
        >
          <FaClock /> Pendientes ({stats.pagosPendientes})
        </button>
      </div>

      {/* Contenido principal */}
      <div className="urbanq-alicuotas-content">
        {/* Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="modern-alert">
            <FaExclamationTriangle className="me-2" />{error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="modern-alert">
            <FaCheckCircle className="me-2" />{success}
          </Alert>
        )}

   {/* Tarjetas de estadísticas - SOLO en tab "Todas" */}
      {activeFilter === 'todos' && (
        <>
          <div className="stats-grid">
            <div className="stat-card paid" onClick={() => filterAlicuotas('Pagado')}>
              <FaCheckCircle style={{fontSize: '3.5rem', color: 'var(--color-success)'}} />
              <div className="stat-content">
                <span className="stat-label">Pagos Realizados</span>
                <span className="stat-value">{stats.pagosRealizados}</span>
                <span className="stat-footer">Ver listado completo</span>
              </div>
            </div>

            <div className="stat-card validation" onClick={() => filterAlicuotas('Pendiente de validación')}>
              <FaClock style={{fontSize: '3.5rem', color: 'var(--color-warning)'}} />
              <div className="stat-content">
                <span className="stat-label">Pendientes Validación</span>
                <span className="stat-value">{stats.pendientesAprobacion}</span>
                <span className="stat-footer">Revisar y aprobar</span>
              </div>
            </div>

            <div className="stat-card pending" onClick={() => filterAlicuotas('Pendiente')}>
              <FaExclamationTriangle style={{fontSize: '3.5rem', color: 'var(--color-danger)'}} />
              <div className="stat-content">
                <span className="stat-label">Pagos Pendientes</span>
                <span className="stat-value">{stats.pagosPendientes}</span>
                <span className="stat-footer">Gestionar pendientes</span>
              </div>
            </div>

            <div className="stat-card total" onClick={() => filterAlicuotas('todos')}>
              <FaFileInvoiceDollar style={{fontSize: '3.5rem', color: 'var(--color-accent-purple)'}} />
              <div className="stat-content">
                <span className="stat-label">Total Alícuotas</span>
                <span className="stat-value">{stats.total}</span>
                <span className="stat-footer">Ver todas</span>
              </div>
            </div>
          </div>

          {/* Barra de Progreso - SOLO en tab "Todas" */}
          <div className="progress-section">
            <div className="progress-header">
              <h3>
                <FaChartLine className="me-2" />
                Progreso General de Cobros
              </h3>
              <span className="progress-percentage">{stats.progreso}% Completado</span>
            </div>
            <div className="modern-progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${stats.progreso}%` }}
              >
                <span className="progress-label">{stats.progreso}%</span>
              </div>
            </div>
            <div className="progress-footer">
              <span>{stats.pagosRealizados} Pagados</span>
              <span>{stats.pendientesAprobacion} En validación</span>
              <span>{stats.pagosPendientes} Pendientes</span>
            </div>
          </div>

          {/* Acciones - SOLO en tab "Todas" */}
          <div className="urbanq-alicuotas-actions">
        <div className="actions-container">
          <button className="create-btn" onClick={() => handleOpenModal()} disabled={loading}>
            <FaPlus className="me-2" />
            Nueva Alícuota
          </button>
          
          <Dropdown className="export-dropdown">
            <Dropdown.Toggle variant="outline-primary" id="dropdown-export">
              <FaFileExport className="me-2" />
              Exportar
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => exportToCSV(filteredAlicuotas)}>
                <FaFileCsv className="me-2" /> Exportar a CSV
              </Dropdown.Item>
              <Dropdown.Item onClick={() => printTable(filteredAlicuotas)}>
                <FaPrint className="me-2" /> Imprimir Tabla
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>


<button 
  className="config-btn"
  onClick={() => setShowPaymentConfigModal(true)} 
  disabled={loading}
  title="Configurar métodos de pago"
>
  <FaQrcode className="me-2" />
  Config. Pagos
</button>





        </div>
          </div>
        </>
      )}

      {/* Sección de tabla - estilo urbanq-support-section */}
      <div className="urbanq-alicuotas-section">
        <div className="table-header">
          <h3>
            <FaFileInvoiceDollar className="me-2" />
            Lista de Alícuotas {activeFilter !== 'todos' && (
              <span className="filter-indicator">
                {activeFilter === 'Pagado' ? '- Pagadas' :
                 activeFilter === 'Pendiente de validación' ? '- En Validación' :
                 activeFilter === 'Pendiente' ? '- Pendientes' : ''}
              </span>
            )}
          </h3>
          <Badge bg="dark" className="count-badge">
            {filteredAlicuotas.length} {filteredAlicuotas.length === 1 ? 'resultado' : 'resultados'}
          </Badge>
        </div>

        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Concepto</th>
                <th>Monto</th>
                <th>Fecha Venc.</th>
                <th>Apto</th>
                <th>Usuario</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && !error ? (
                <tr>
                  <td colSpan="8" className="loading-cell">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando alícuotas...
                  </td>
                </tr>
              ) : displayedAlicuotas.length > 0 ? (
                displayedAlicuotas.map(alicuota => (
                  <tr key={alicuota.id} className="table-row">
                    <td className="id-cell">{alicuota.id}</td>
                    <td className="concept-cell">
                      <div className="concept-wrapper">
                        <FaFileInvoiceDollar className="concept-icon" />
                        <span>{alicuota.concepto}</span>
                      </div>
                    </td>
                    <td className="amount-cell">{formatCurrency(alicuota.monto)}</td>
                    <td className="date-cell">
                      <FaCalendarAlt className="me-2" />
                      {alicuota.fechaVencimiento}
                    </td>
                    <td className="apartment-cell">
                      <Badge bg="light" text="dark" className="apartment-badge">
                        <FaBuilding className="me-1" />
                        {alicuota.apartamento}
                      </Badge>
                    </td>
                    <td className="user-cell">
                      <div className="user-wrapper">
                        <div className="user-avatar">
                          {alicuota.usuario.charAt(0).toUpperCase()}
                        </div>
                        <span>{alicuota.usuario}</span>
                      </div>
                    </td>
                    <td className="status-cell">
                      <Badge 
                        className={`status-badge ${
                          alicuota.estado === 'Pagado' ? 'paid' :
                          alicuota.estado === 'Pendiente de validación' ? 'validation' :
                          'pending'
                        }`}
                      >
                        {alicuota.estado}
                      </Badge>
                    </td>
                    <td className="actions-cell">
                      {alicuota.estado === 'Pendiente de validación' ? (
                        <>
                          <button 
                            className="action-btn approve" 
                            onClick={() => handleOpenApproveModal(alicuota)}
                            disabled={loading}
                            title="Aprobar pago"
                          >
                            <FaCheck />
                          </button>
                          <button 
                            className="action-btn reject" 
                            onClick={() => handleRejectPayment(alicuota)}
                            disabled={loading}
                            title="Rechazar pago"
                          >
                            <FaTimes />
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="action-btn edit" 
                            onClick={() => handleOpenModal(alicuota)}
                            disabled={loading}
                            title="Editar alícuota"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            className="action-btn delete" 
                            onClick={() => handleDeleteAlicuota(alicuota.id)}
                            disabled={alicuota.estado === 'Pagado' || loading}
                            title={alicuota.estado === 'Pagado' ? "No se puede eliminar un pago realizado" : "Eliminar alícuota"}
                          >
                            <FaTrash />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="empty-cell">
                    <div className="empty-state">
                      <FaFileInvoiceDollar className="empty-icon" />
                      <p>No hay alícuotas que coincidan con el filtro</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {filteredAlicuotas.length > 0 && (
          <div className="table-footer">
            <div className="pagination-info">
              Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, filteredAlicuotas.length)} a {Math.min(currentPage * itemsPerPage, filteredAlicuotas.length)} de {filteredAlicuotas.length} registros
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FaChevronLeft />
              </button>
              <span className="page-info">
                Página {currentPage} de {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Crear/Editar */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        backdrop="static" 
        size="lg"
        className="modern-modal"
        centered
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>
            <FaFileInvoiceDollar className="me-2" />
            {isEditMode ? 'Editar Alícuota' : 'Crear Nueva Alícuota'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {loading && (
            <div className="loading-overlay">
              <Spinner animation="border" variant="primary" className="me-2" />
              <span>Guardando...</span>
            </div>
          )}
          
          <Form onSubmit={handleSubmit} id="alicuotaForm">
            <div className="form-grid">
              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaFileInvoiceDollar className="me-2" />
                  Concepto *
                </Form.Label>
                <Form.Control 
                  type="text" 
                  name="concepto" 
                  value={newAlicuotaData.concepto} 
                  onChange={handleInputChange} 
                  placeholder="Ej: Alícuota Enero" 
                  className="modern-form-input"
                  required 
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaDollarSign className="me-2" />
                  Monto ($) *
                </Form.Label>
                <Form.Control 
                  type="number" 
                  step="0.01" 
                  min="0" 
                  name="monto" 
                  value={newAlicuotaData.monto} 
                  onChange={handleInputChange} 
                  placeholder="Ej: 50.00" 
                  className="modern-form-input"
                  required 
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaCalendarAlt className="me-2" />
                  Fecha de Vencimiento
                </Form.Label>
                <Form.Control 
                  type="date" 
                  name="fechaVencimiento" 
                  value={newAlicuotaData.fechaVencimiento} 
                  onChange={handleInputChange} 
                  className="modern-form-input"
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="form-group-modern">
                <Form.Label>
                  <FaUser className="me-2" />
                  Usuario (Inquilino) *
                </Form.Label>
                <Form.Select 
                  name="usuarioId" 
                  value={newAlicuotaData.usuarioId} 
                  onChange={handleInputChange} 
                  className="modern-form-select"
                  required 
                  disabled={loadingUsers || users.length === 0 || loading}
                >
                  <option value="">
                    {loadingUsers ? 'Cargando...' : (users.length === 0 ? 'No hay usuarios' : '-- Seleccione --')}
                  </option>
                  {!loadingUsers && users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} (Apto: {user.apartment || 'N/A'})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="form-group-modern full-width">
                <Form.Label>Descripción (Opcional)</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  name="descripcion" 
                  value={newAlicuotaData.descripcion} 
                  onChange={handleInputChange} 
                  placeholder="Detalles adicionales..." 
                  className="modern-form-input"
                  disabled={loading}
                />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button variant="secondary" onClick={handleCloseModal} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            form="alicuotaForm" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Guardando...' : (isEditMode ? 'Guardar Cambios' : 'Crear Alícuota')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Aprobar */}
      <Modal 
        show={showApproveModal} 
        onHide={handleCloseApproveModal} 
        centered
        className="modern-modal approve-modal"
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>
            <FaCheckCircle className="me-2" />
            Confirmar Aprobación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {selectedAlicuota && (
            <div className="approve-content">
              <div className="approve-icon">
                <FaCheckCircle />
              </div>
              <p className="approve-text">
                ¿Confirmar aprobación del pago de <strong>{formatCurrency(selectedAlicuota.monto)}</strong> por <strong>"{selectedAlicuota.concepto}"</strong>?
              </p>
              <div className="approve-details">
                <div className="detail-row">
                  <span className="detail-label">Usuario:</span>
                  <span className="detail-value">{selectedAlicuota.usuario}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Apartamento:</span>
                  <span className="detail-value">{selectedAlicuota.apartamento}</span>
                </div>
              </div>
              <p className="approve-warning">
                Esta acción marcará la alícuota como 'Pagado'.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button variant="secondary" onClick={handleCloseApproveModal} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            variant="success" 
            onClick={handleApprovePayment} 
            className="approve-btn"
            disabled={loading}
          >
            <FaCheck className="me-2" />
            {loading ? 'Aprobando...' : 'Sí, Aprobar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* NUEVO: Modal de Configuración de Pago */}
      <PaymentConfigModal
        show={showPaymentConfigModal}
        onHide={() => {
          if (paymentConfigExists) {
            setShowPaymentConfigModal(false);
          }
        }}
        onConfigSaved={handlePaymentConfigSaved}
      />
      </div>{/* Cierre urbanq-alicuotas-content */}
    </div>
  );
};

export default AdminAlicuotas;